import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, RiskBadge, StatusBadge, EmptyState } from "@/components/common/Bits";
import { fmtDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Radar, ArrowRight, Plus, Users, Activity } from "lucide-react";
import { toast } from "sonner";

const SHADOW_STATUSES = ["No gobernada", "En análisis", "En registro", "Registrada", "Bloqueada", "Excepción autorizada"];

export default function ShadowAI() {
  const { can } = useAuth();
  const [rows, setRows] = useState(null);
  const load = () => api.get("/shadow").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);

  if (!rows) return <div className="grid sm:grid-cols-2 gap-4">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-52 rounded-xl" />)}</div>;

  return (
    <div>
      <PageHeader title="Shadow AI · Observabilidad" subtitle="Detecciones de herramientas de IA utilizadas sin registro formal. Convierta detecciones en iniciativas gobernadas conservando la trazabilidad de origen." testId="shadow-header">
        {can.edit && <NewDetectionDialog onSaved={load} />}
      </PageHeader>

      {rows.length === 0 && <EmptyState icon={Radar} title="Sin detecciones" />}
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {rows.map((s) => (
          <Card key={s.id} className="shadow-card border-border flex flex-col" data-testid={`shadow-card-${s.id}`}>
            <CardContent className="p-5 flex flex-col flex-1">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-10 w-10 rounded-xl bg-risk-critical/8 text-risk-critical flex items-center justify-center shrink-0"><Radar className="h-5 w-5" /></div>
                  <div className="min-w-0">
                    <p className="font-display font-semibold leading-tight truncate">{s.app_name}</p>
                    <p className="text-xs text-muted-foreground truncate">{s.department} · {s.probable_provider}</p>
                  </div>
                </div>
                <RiskBadge level={s.estimated_risk} />
              </div>

              <div className="grid grid-cols-2 gap-3 mt-4 text-xs">
                <span className="flex items-center gap-1.5 text-muted-foreground"><Users className="h-3.5 w-3.5" /> {s.observed_users} usuarios</span>
                <span className="flex items-center gap-1.5 text-muted-foreground"><Activity className="h-3.5 w-3.5" /> {s.request_volume}</span>
                <span className="text-muted-foreground col-span-2">Fuente: {s.source} · Última actividad {fmtDate(s.last_activity)}</span>
              </div>
              <p className="text-xs text-muted-foreground mt-3 line-clamp-2 leading-relaxed">{s.evidence}</p>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {(s.observed_data_types || []).map((t) => <span key={t} className="px-2 py-0.5 rounded bg-secondary text-secondary-foreground text-[10px] font-medium">{t}</span>)}
              </div>

              <div className="mt-auto pt-4 flex items-center justify-between gap-2">
                {can.edit && !s.converted_initiative_id ? (
                  <Select value={s.governance_status} onValueChange={async (v) => {
                    try { await api.put(`/shadow/${s.id}`, { governance_status: v }); toast.success("Estado actualizado"); load(); } catch (e) { toast.error(apiError(e)); }
                  }}>
                    <SelectTrigger className="h-8 w-auto text-xs bg-card" data-testid={`shadow-status-${s.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{SHADOW_STATUSES.map((st) => <SelectItem key={st} value={st}>{st}</SelectItem>)}</SelectContent>
                  </Select>
                ) : <StatusBadge status={s.governance_status} />}

                {s.converted_initiative_id ? (
                  <Button asChild size="sm" variant="outline" className="h-8 text-xs border-border">
                    <Link to={`/iniciativas/${s.converted_initiative_id}`}>Ver iniciativa <ArrowRight className="h-3.5 w-3.5 ml-1" /></Link>
                  </Button>
                ) : can.edit && (
                  <ConvertDialog detection={s} onSaved={load} />
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

const ConvertDialog = ({ detection, onSaved }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(`Regularización ${detection.app_name}`);
  const [owner, setOwner] = useState("");
  const convert = async () => {
    if (!owner) { toast.error("Indique el business owner de la iniciativa"); return; }
    try {
      const { data } = await api.post(`/shadow/${detection.id}/convert`, {
        data: { name, business_owner: owner, description: `Regularización de uso detectado: ${detection.evidence}`, provider: detection.probable_provider || "Interno UC CHRISTUS", data_types: detection.observed_data_types, estimated_users: detection.observed_users, usage_volume: detection.request_volume, asset_type: "Herramienta externa" },
      });
      toast.success(`Iniciativa ${data.code} creada con trazabilidad de origen Shadow AI`);
      setOpen(false);
      onSaved();
      navigate(`/iniciativas/${data.id}`);
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="h-8 text-xs bg-primary hover:bg-primary/90 text-primary-foreground" data-testid={`convert-btn-${detection.id}`}>
          Convertir en iniciativa <ArrowRight className="h-3.5 w-3.5 ml-1" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-display">Convertir detección en iniciativa</DialogTitle>
          <DialogDescription>Se conservará el vínculo con la detección {detection.id} y su trazabilidad de origen.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5"><Label className="text-xs">Nombre de la iniciativa</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="convert-name-input" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Business owner</Label><Input value={owner} onChange={(e) => setOwner(e.target.value)} placeholder="Responsable del área" data-testid="convert-owner-input" /></div>
        </div>
        <DialogFooter><Button onClick={convert} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="convert-submit-btn">Crear iniciativa</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const NewDetectionDialog = ({ onSaved }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ app_name: "", department: "", source: "Reporte manual", estimated_risk: "Medio", observed_users: 1, evidence: "", probable_provider: "" });
  const save = async () => {
    if (!f.app_name || !f.department) { toast.error("Nombre de aplicación y departamento son obligatorios"); return; }
    try {
      await api.post("/shadow", { data: { ...f, observed_users: Number(f.observed_users) || 1 } });
      toast.success("Detección registrada");
      setOpen(false);
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="new-detection-btn"><Plus className="h-4 w-4 mr-2" /> Registrar detección</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-display">Registrar detección de Shadow AI</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5 col-span-2"><Label className="text-xs">Aplicación</Label><Input value={f.app_name} onChange={(e) => setF({ ...f, app_name: e.target.value })} data-testid="detection-name-input" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Departamento</Label><Input value={f.department} onChange={(e) => setF({ ...f, department: e.target.value })} data-testid="detection-dept-input" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Proveedor probable</Label><Input value={f.probable_provider} onChange={(e) => setF({ ...f, probable_provider: e.target.value })} /></div>
          <div className="space-y-1.5"><Label className="text-xs">Usuarios observados</Label><Input type="number" value={f.observed_users} onChange={(e) => setF({ ...f, observed_users: e.target.value })} /></div>
          <div className="space-y-1.5"><Label className="text-xs">Riesgo estimado</Label>
            <Select value={f.estimated_risk} onValueChange={(v) => setF({ ...f, estimated_risk: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{["Bajo", "Medio", "Medio-Alto", "Alto", "Crítico"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5 col-span-2"><Label className="text-xs">Evidencia</Label><Textarea rows={2} value={f.evidence} onChange={(e) => setF({ ...f, evidence: e.target.value })} /></div>
        </div>
        <DialogFooter><Button onClick={save} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="detection-save-btn">Registrar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
